import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Wallet,
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Calendar,
  Landmark,
  Receipt,
} from "lucide-react";

import api from "../api";

import "../Styles/CashJournal.css";

// ============================================
// CASH ACCOUNTS
// ============================================

const CASH_ACCOUNT_IDS = [13, 14];

const CashJournal = () => {

  const navigate = useNavigate();

  const now = new Date();

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedMonth, setSelectedMonth] =
    useState(now.getMonth());

  const [selectedYear, setSelectedYear] =
    useState(now.getFullYear());

  // ============================================
  // LOAD DATA
  // ============================================

  useEffect(() => {

    const loadData = async () => {

      try {

        setLoading(true);

        const combined = [];

        for (const accountId of CASH_ACCOUNT_IDS) {

          const txns =
            await api.getTransactions(accountId);

          txns.forEach((txn) => {

            combined.push({
              ...txn,
              accountId,
            });

          });

        }

        combined.sort(
          (a, b) =>
            new Date(a.date) - new Date(b.date)
        );

        setTransactions(combined);

      } catch (err) {

        console.error(
          "Failed loading cash journal:",
          err
        );

      } finally {

        setLoading(false);

      }

    };

    loadData();

  }, []);

  // ============================================
  // FILTERED TRANSACTIONS
  // ============================================

  const filteredTransactions = useMemo(() => {

    return transactions.filter((txn) => {

      const txnDate = new Date(txn.date);

      return (
        txnDate.getMonth() === selectedMonth &&
        txnDate.getFullYear() === selectedYear
      );

    });

  }, [transactions, selectedMonth, selectedYear]);

  // ============================================
  // RECEIPTS / PAYMENTS
  // ============================================

  const receipts = filteredTransactions.filter(
    (t) => parseFloat(t.debit || 0) > 0
  );

  const payments = filteredTransactions.filter(
    (t) => parseFloat(t.credit || 0) > 0
  );

  // ============================================
  // TOTALS
  // ============================================

  const totalReceipts = receipts.reduce(
    (sum, t) =>
      sum + parseFloat(t.debit || 0),
    0
  );

  const totalPayments = payments.reduce(
    (sum, t) =>
      sum + parseFloat(t.credit || 0),
    0
  );

  const balanceCF =
    totalReceipts - totalPayments;

  // ============================================
  // LOADING
  // ============================================

  if (loading) {

    return (

      <div className="cash-page">

        <div className="cash-loading-card">

          <div className="cash-spinner" />

          <p>Loading Cash Journal...</p>

        </div>

      </div>

    );

  }

  // ============================================
  // UI
  // ============================================

  return (

    <div className="cash-page">

      {/* ========================================
          HERO
      ======================================== */}

      <div className="cash-hero">

        <div className="cash-hero-left">

          <div className="cash-pill">

            <Wallet size={15} />

            <span>Cash Journal</span>

          </div>

          <h1>
            Cash
            <br />
            Management
          </h1>

          <p>
            Monitor receipts, payments,
            operational cash movement and
            monthly balancing across
            business cash accounts.
          </p>

          <div className="cash-actions">

            <button
              className="cash-btn primary"
              onClick={() =>
                navigate("/journals")
              }
            >
              <ArrowLeft size={16} />
              Back to Journals
            </button>

          </div>

        </div>

        {/* ========================================
            RIGHT SIDE SUMMARY
        ======================================== */}

        <div className="cash-summary-grid">

          <div className="summary-card green">

            <div className="summary-icon">
              <TrendingUp size={24} />
            </div>

            <div>

              <span>Total Receipts</span>

              <h2>
                $
                {totalReceipts.toLocaleString()}
              </h2>

            </div>

          </div>

          <div className="summary-card red">

            <div className="summary-icon">
              <TrendingDown size={24} />
            </div>

            <div>

              <span>Total Payments</span>

              <h2>
                $
                {totalPayments.toLocaleString()}
              </h2>

            </div>

          </div>

          <div className="summary-card teal">

            <div className="summary-icon">
              <Landmark size={24} />
            </div>

            <div>

              <span>Balance c/f</span>

              <h2>
                $
                {balanceCF.toLocaleString()}
              </h2>

            </div>

          </div>

          <div className="summary-card blue">

            <div className="summary-icon">
              <Calendar size={24} />
            </div>

            <div>

              <span>Period</span>

              <h2>
                {new Date(
                  selectedYear,
                  selectedMonth
                ).toLocaleString(
                  "en-US",
                  {
                    month: "long",
                    year: "numeric",
                  }
                )}
              </h2>

            </div>

          </div>

        </div>

      </div>

      {/* ========================================
          FILTER BAR
      ======================================== */}

      <div className="cash-filter-bar">

        <div className="filter-group">

          <label>Month</label>

          <select
            value={selectedMonth}
            onChange={(e) =>
              setSelectedMonth(
                parseInt(e.target.value)
              )
            }
          >

            {Array.from(
              { length: 12 },
              (_, i) => (

                <option
                  key={i}
                  value={i}
                >
                  {new Date(
                    0,
                    i
                  ).toLocaleString(
                    "en-US",
                    {
                      month: "long",
                    }
                  )}
                </option>

              )
            )}

          </select>

        </div>

        <div className="filter-group">

          <label>Year</label>

          <select
            value={selectedYear}
            onChange={(e) =>
              setSelectedYear(
                parseInt(e.target.value)
              )
            }
          >

            {Array.from(
              { length: 5 },
              (_, i) => {

                const year =
                  now.getFullYear() - 2 + i;

                return (
                  <option
                    key={year}
                    value={year}
                  >
                    {year}
                  </option>
                );

              }
            )}

          </select>

        </div>

      </div>

      {/* ========================================
          JOURNAL TABLES
      ======================================== */}

      <div className="cash-journal-grid">

        {/* ====================================
            RECEIPTS
        ==================================== */}

        <div className="cash-journal-card">

          <div className="journal-header receipts">

            <Receipt size={20} />

            <h3>Receipts Journal</h3>

          </div>

          <table className="cash-table">

            <thead>

              <tr>
                <th>Date</th>
                <th>Particulars</th>
                <th>Debit</th>
              </tr>

            </thead>

            <tbody>

              {receipts.map((txn, index) => (

                <tr key={index}>

                  <td>
                    {txn.date?.split("T")[0]}
                  </td>

                  <td>
                    By {txn.details}
                  </td>

                  <td className="money debit">
                    $
                    {parseFloat(
                      txn.debit
                    ).toLocaleString()}
                  </td>

                </tr>

              ))}

              <tr className="table-total">

                <td colSpan="2">
                  Total Receipts
                </td>

                <td>
                  $
                  {totalReceipts.toLocaleString()}
                </td>

              </tr>

            </tbody>

          </table>

        </div>

        {/* ====================================
            PAYMENTS
        ==================================== */}

        <div className="cash-journal-card">

          <div className="journal-header payments">

            <Receipt size={20} />

            <h3>Payments Journal</h3>

          </div>

          <table className="cash-table">

            <thead>

              <tr>
                <th>Date</th>
                <th>Particulars</th>
                <th>Credit</th>
              </tr>

            </thead>

            <tbody>

              {payments.map((txn, index) => (

                <tr key={index}>

                  <td>
                    {txn.date?.split("T")[0]}
                  </td>

                  <td>
                    To {txn.details}
                  </td>

                  <td className="money credit">
                    $
                    {parseFloat(
                      txn.credit
                    ).toLocaleString()}
                  </td>

                </tr>

              ))}

              <tr className="table-total">

                <td colSpan="2">
                  Total Payments
                </td>

                <td>
                  $
                  {totalPayments.toLocaleString()}
                </td>

              </tr>

              <tr className="table-balance">

                <td colSpan="2">
                  Balance c/f
                </td>

                <td>
                  $
                  {balanceCF.toLocaleString()}
                </td>

              </tr>

            </tbody>

          </table>

        </div>

      </div>

    </div>

  );

};

export default CashJournal;